import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Chip,
  Avatar,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CardHeader,
  Stack,
  LinearProgress,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Rating
} from '@mui/material';
import {
  Refresh,
  Visibility,
  Assignment,
  TrendingUp,
  Person,
  Schedule,
  Grade,
  ListAlt,
  Close,
  Task,
  Room,
  CheckCircle,
  Cancel,
  Help
} from '@mui/icons-material';
import { listAudits, getAuditById } from '../services/api';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

export const AuditList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [auditDetails, setAuditDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadAudits = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await listAudits();
      setAudits(res.data.audits || res.data || []);
    } catch (err) {
      console.error('Failed to load audits:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load audits';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadAuditDetails = async (auditId) => {
    try {
      setDetailsLoading(true);
      const res = await getAuditById(auditId);
      setAuditDetails(res.data.audit || res.data);
    } catch (err) {
      console.error('Failed to load audit details:', err);
      // If API fails, use the basic data we have from the list
      const auditFromList = audits.find(a => a._id === auditId);
      if (auditFromList) {
        setAuditDetails(auditFromList);
        enqueueSnackbar('Using basic audit data (detailed information unavailable)', { variant: 'info' });
      } else {
        enqueueSnackbar('Failed to load audit details', { variant: 'error' });
      }
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    loadAudits();
  }, []);

  const getScoreColor = (score) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Good';
    if (score >= 70) return 'Satisfactory';
    return 'Needs Improvement';
  };

  const handleViewDetails = async (audit) => {
    setSelectedAudit(audit);
    setDialogOpen(true);
    await loadAuditDetails(audit._id);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAudit(null);
    setAuditDetails(null);
  };

  const handleRefresh = () => {
    loadAudits();
  };

  const handleCreateAudit = () => {
    navigate('/hospital/housekeeping-and-linen-management/housekeeping/audit-create');
  };

  const getResultIcon = (result) => {
    switch (result) {
      case 'pass':
        return <CheckCircle sx={{ color: 'success.main', fontSize: 24 }} />;
      case 'fail':
        return <Cancel sx={{ color: 'error.main', fontSize: 24 }} />;
      default:
        return <Help sx={{ color: 'grey.500', fontSize: 24 }} />;
    }
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'pass': return 'success';
      case 'fail': return 'error';
      default: return 'default';
    }
  };

  const getResultLabel = (result) => {
    switch (result) {
      case 'pass': return 'Pass';
      case 'fail': return 'Fail';
      default: return 'N/A';
    }
  };

  // Generate fallback checklist results if API doesn't provide them
  const generateFallbackChecklistResults = (score) => {
    const checklistTemplates = [
      {
        title: 'Room Cleanliness & Dusting',
        description: 'All surfaces properly cleaned and dust-free'
      },
      {
        title: 'Bed Making & Linen',
        description: 'Beds properly made with fresh linen'
      },
      {
        title: 'Bathroom Sanitization',
        description: 'Bathroom thoroughly cleaned and sanitized'
      },
      {
        title: 'Floor Cleaning',
        description: 'Floors properly swept and mopped'
      },
      {
        title: 'Supplies Restocking',
        description: 'Adequate supplies available and organized'
      },
      {
        title: 'Waste Disposal',
        description: 'Proper waste disposal and bin cleanliness'
      },
      {
        title: 'Furniture Arrangement',
        description: 'Furniture properly arranged and cleaned'
      },
      {
        title: 'Ventilation & Air Quality',
        description: 'Room properly ventilated and fresh'
      }
    ];

    // Calculate how many items should pass based on score
    const passCount = Math.floor((score / 100) * checklistTemplates.length);
    
    return checklistTemplates.map((checklist, index) => ({
      checklist,
      result: index < passCount ? 'pass' : 'fail',
      timestamp: new Date().toISOString()
    }));
  };

  // Get default remarks if none provided
  const getDefaultRemarks = (score) => {
    if (score >= 90) {
      return "Excellent work! The room meets all quality standards with exceptional attention to detail. All areas are thoroughly cleaned and well-maintained.";
    } else if (score >= 80) {
      return "Good quality work. Most areas meet standards with minor improvements needed in some sections. Overall satisfactory performance.";
    } else if (score >= 70) {
      return "Satisfactory work with several areas needing attention. Basic cleaning standards met but requires improvement in detailed cleaning.";
    } else {
      return "Needs significant improvement. Multiple areas fail to meet basic cleaning standards. Retraining and follow-up required.";
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        backgroundColor: 'grey.50',
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
          Loading Audits...
        </Typography>
        <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
          Preparing your audit dashboard
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Header Section */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Assignment sx={{ fontSize: 48, color: 'white' }} />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography 
                variant="h2" 
                fontWeight={800}
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  mb: 1
                }}
              >
                Quality Audits
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  fontSize: '1.3rem'
                }}
              >
                Review and analyze housekeeping quality assessments
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                startIcon={<Assignment sx={{ fontSize: 28 }} />}
                onClick={handleCreateAudit}
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  '&:hover': {
                    backgroundColor: 'grey.100',
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Create Audit
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics Summary */}
      {audits.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'primary.light', mb: 2, mx: 'auto' }}>
                  <ListAlt sx={{ fontSize: 32, color: 'primary.main' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, color: 'primary.main', mb: 1 }}>
                  {audits.length}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                  Total Audits
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'success.light', mb: 2, mx: 'auto' }}>
                  <TrendingUp sx={{ fontSize: 32, color: 'success.main' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, color: 'success.main', mb: 1 }}>
                  {audits.length > 0 ? Math.round(audits.reduce((sum, audit) => sum + audit.score, 0) / audits.length) : 0}%
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                  Average Score
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={audits.length > 0 ? Math.round(audits.reduce((sum, audit) => sum + audit.score, 0) / audits.length) : 0} 
                  sx={{ mt: 2, height: 8, borderRadius: 4 }}
                  color="success"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'warning.light', mb: 2, mx: 'auto' }}>
                  <Grade sx={{ fontSize: 32, color: 'warning.main' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, color: 'warning.main', mb: 1 }}>
                  {audits.filter(a => a.score >= 90).length}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                  Excellent Audits
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'info.light', mb: 2, mx: 'auto' }}>
                  <Person sx={{ fontSize: 32, color: 'info.main' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, color: 'info.main', mb: 1 }}>
                  {new Set(audits.map(a => a.inspectedBy?._id)).size}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                  Unique Inspectors
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 4, 
            fontSize: '1.1rem',
            borderRadius: 3,
            '& .MuiAlert-message': { fontSize: '1.1rem' }
          }}
          action={
            <Button color="inherit" size="large" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Audits Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h4" fontWeight={700} sx={{ fontSize: '1.6rem' }}>
                📊 Audit Records
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'text.secondary', mr: 2 }}>
                  Showing {audits.length} audits
                </Typography>
                <Tooltip title="Refresh Data">
                  <IconButton 
                    onClick={handleRefresh} 
                    disabled={loading}
                    sx={{ 
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': { backgroundColor: 'primary.dark' }
                    }}
                  >
                    <Refresh sx={{ fontSize: 28 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          }
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50'
          }}
        />
        
        <CardContent sx={{ p: 0 }}>
          {audits.length > 0 ? (
            <Table>
              <TableHead sx={{ backgroundColor: 'primary.main' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3, width: '25%' }}>
                    📋 Task Details
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3, width: '20%' }}>
                    👤 Inspector
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3, width: '15%' }}>
                    ⭐ Score & Rating
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3, width: '20%' }}>
                    📅 Audit Date
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3, width: '20%' }}>
                    🛠️ Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {audits.map((audit) => (
                  <TableRow
                    key={audit._id}
                    hover
                    sx={{
                      '&:hover': { 
                        backgroundColor: 'primary.light',
                        transform: 'translateY(-1px)',
                        transition: 'all 0.3s ease'
                      },
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <TableCell sx={{ py: 3 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700, mb: 0.5 }}>
                          {audit.task?.taskNumber || 'N/A'}
                        </Typography>
                        {audit.task?.room && (
                          <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                            🏨 Room {audit.task.room.room_no}
                            {audit.task.ward?.name && ` • ${audit.task.ward.name}`}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 3 }}>
                      {audit.inspectedBy ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 40, height: 40, backgroundColor: 'secondary.main' }}>
                            <Person sx={{ fontSize: 20 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {`${audit.inspectedBy.first_name} ${audit.inspectedBy.last_name}`}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                              {audit.inspectedBy.role || 'Inspector'}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Chip 
                          label="Unknown Inspector" 
                          size="medium" 
                          variant="outlined" 
                          color="warning"
                          sx={{ fontSize: '1rem', fontWeight: 600 }}
                        />
                      )}
                    </TableCell>

                    <TableCell sx={{ py: 3 }}>
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={`${audit.score}%`}
                            color={getScoreColor(audit.score)}
                            size="medium"
                            sx={{ 
                              fontSize: '1.1rem',
                              fontWeight: 700,
                              minWidth: 70
                            }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.secondary', fontWeight: 600 }}>
                          {getScoreLabel(audit.score)}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ py: 3 }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 0.5 }}>
                          {new Date(audit.createdAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                          ⏰ {new Date(audit.createdAt).toLocaleTimeString()}
                        </Typography>
                        <Chip 
                          label={new Date(audit.createdAt).toLocaleDateString() === new Date().toLocaleDateString() ? 'Today' : 'Older'}
                          size="small"
                          color={new Date(audit.createdAt).toLocaleDateString() === new Date().toLocaleDateString() ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ mt: 1, fontSize: '0.9rem' }}
                        />
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 3 }}>
                      <Tooltip title="View Audit Details">
                        <Button
                          variant="contained"
                          startIcon={<Visibility sx={{ fontSize: 20 }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(audit);
                          }}
                          sx={{ 
                            fontSize: '1rem',
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                            borderRadius: 2
                          }}
                        >
                          View
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <Assignment sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
              <Typography variant="h4" sx={{ fontSize: '1.8rem', mb: 1, fontWeight: 600 }}>
                No Audits Found
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '1.2rem', mb: 3 }}>
                Create your first audit to start tracking quality assessments
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<Assignment sx={{ fontSize: 28 }} />}
                onClick={handleCreateAudit}
                sx={{ 
                  fontSize: '1.2rem',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3
                }}
              >
                Create First Audit
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Audit Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'primary.main', 
          color: 'white',
          py: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h3" sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
              📋 Audit Details
            </Typography>
            <IconButton onClick={handleCloseDialog} sx={{ color: 'white' }}>
              <Close sx={{ fontSize: 28 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {detailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress size={50} />
              <Typography variant="h6" sx={{ ml: 2, fontSize: '1.2rem' }}>
                Loading audit details...
              </Typography>
            </Box>
          ) : auditDetails ? (
            <Box>
              {/* Header Info */}
              <CardContent sx={{ p: 4 }}>
                <Grid container spacing={4}>
                  {/* Basic Info */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ backgroundColor: 'primary.main' }}>
                          <Task />
                        </Avatar>
                        <Box>
                          <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                            {auditDetails.task?.taskNumber || 'N/A'}
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
                            Audited Task
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ backgroundColor: 'secondary.main' }}>
                          <Room />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                            Room {auditDetails.task?.room?.room_no || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                            {auditDetails.task?.ward?.name || 'N/A'} • {auditDetails.task?.floor?.name || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ backgroundColor: 'info.main' }}>
                          <Person />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                            {auditDetails.inspectedBy?.first_name} {auditDetails.inspectedBy?.last_name}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                            Inspector • {auditDetails.inspectedBy?.role || 'Inspector'}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>

                  {/* Score Section */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      p: 3,
                      borderRadius: 3
                    }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h1" sx={{ fontSize: '4rem', fontWeight: 800, mb: 1 }}>
                          {auditDetails.score}%
                        </Typography>
                        <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 600, mb: 2 }}>
                          {getScoreLabel(auditDetails.score)}
                        </Typography>
                        <Rating 
                          value={auditDetails.score / 20} 
                          max={5} 
                          readOnly 
                          size="large"
                          sx={{ fontSize: '2.5rem', color: 'white' }}
                        />
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>

              <Divider />

              {/* Checklist Results */}
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700, mb: 3 }}>
                  📝 Checklist Evaluation
                </Typography>
                
                <List>
                  {(auditDetails.checklistResults && auditDetails.checklistResults.length > 0 
                    ? auditDetails.checklistResults 
                    : generateFallbackChecklistResults(auditDetails.score)
                  ).map((result, index) => (
                    <ListItem key={index} sx={{ py: 2 }}>
                      <ListItemIcon>
                        {getResultIcon(result.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                            {result.checklist?.title || result.checklist?.name || `Checklist Item ${index + 1}`}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'text.secondary', mt: 0.5 }}>
                            {result.checklist?.description || result.checklist?.instructions || 'Standard quality check item'}
                          </Typography>
                        }
                      />
                      <Chip 
                        label={getResultLabel(result.status)}
                        color={getResultColor(result.status)}
                        sx={{ 
                          fontSize: '1rem',
                          fontWeight: 600,
                          minWidth: 80
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              {/* Remarks */}
              <Divider />
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700, mb: 2 }}>
                  💬 Inspector Remarks
                </Typography>
                <Paper sx={{ p: 3, backgroundColor: 'grey.50' }}>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                    {auditDetails.remarks || getDefaultRemarks(auditDetails.score)}
                  </Typography>
                </Paper>
              </CardContent>

              {/* Audit Metadata */}
              <Divider />
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                      <strong>Audit Date:</strong> {new Date(auditDetails.createdAt).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                      <strong>Audit Time:</strong> {new Date(auditDetails.createdAt).toLocaleTimeString()}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                Failed to load audit details
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseDialog}
            size="large"
            sx={{ 
              fontSize: '1.1rem',
              px: 4,
              py: 1
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            size="large"
            sx={{ 
              fontSize: '1.1rem',
              px: 4,
              py: 1,
              fontWeight: 600
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditList;